import React, { useEffect, useRef, useState } from 'react'
import { Link } from '@inertiajs/react'
import { X } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { apiFetch } from '@/lib/csrf'
import { announcementIcon } from '@/lib/announcements'
import type { AnnouncementData } from '@/types/shared'

// THE SPOTLIGHT — the third treatment (docs/plans/2026-08-14-product-announcements.md §13), and
// AnnouncementCard's sibling in every way that matters. Same table, same dismissal rows, same
// three-verb census, same feed row, same fallback chain; the treatment is derived from `kind`
// alone (§13.1), never from a second flag. `milestone` earns centre stage — the beta thank-you, an
// era change, a handful a year. Anything routine is still a card or a feed line, never this.
//
// TREATMENTS NEVER SUPPRESS EACH OTHER (§13.2, owner decision 2026-08-20). A pending Spotlight and
// a pending card may render on the same page load and neither candidate query knows the other
// exists. That is what the z-index says out loud: the backdrop is z-40, deliberately ABOVE the
// card's z-30 (so a pending card waits underneath and is revealed when this closes) and BELOW real
// dialogs at z-50 (so a Radix dialog the operator opened still wins). Never "fix" a stacking
// surprise by moving this to z-50 — that is the layer that belongs to work the operator started.
//
// TRIVIALLY ESCAPABLE, AND EVERY EXIT PERSISTS (§13.3). ✕, Esc, a backdrop click and both CTAs all
// write the dismissal. A "modal" that reappears on the next page load is nagging rather than
// announcing, so there is no exit from this component that only hides it locally.
//
// THE COVER MAY MEET THE CURSOR (§13.4). Same `<iframe srcDoc sandbox="allow-scripts">` as the
// card — no `allow-same-origin`, so the art still cannot touch our cookies, storage or DOM, and
// cannot navigate or phone home — but WITHOUT the card's `pointer-events-none` wrapper: Spotlight
// art may react to the mouse (parallax, light). The sandbox is the guarantee, not the wrapper, and
// every real control stays in the shell below. 16:9 canvas here, against the card's 12:5.
//
// NO LOGO IN COVER ART (§13.7). The current mark is temporary, so the gradient fallback here is
// gradient only — the card's three-bar wordmark is deliberately NOT reused.
//
// EVERY HREF IS BACKEND-SERVED. This file appends nothing to any of them.
//
// THE CENSUS (§12). Three beacons — `seen` on mount, `learn_more_click`, `cta_click` — posted to
// the backend-served `eventsHref`, first-occurrence-only server-side, strictly fire-and-forget:
// never awaited, never blocking, every failure swallowed. Nothing the beacons write changes what
// this renders; dismissal alone decides that (§12 rule 3).

interface SpotlightAnnouncementProps {
  announcement: AnnouncementData
}

/**
 * The 16:9 art box (§13.4). Same aspect whether the cover exists or not, so the Spotlight does not
 * resize when a milestone ships without art.
 */
function SpotlightCover({ coverHtml, title }: { coverHtml: string | null; title: string }) {
  // NOTE the absence of `pointer-events-none` — that is the §13.4 exception, not an oversight.
  const box = 'relative w-full overflow-hidden bg-slate-950'
  const aspect = { aspectRatio: '16 / 9' }

  if (!coverHtml) {
    return (
      <div className={box} style={aspect} data-testid="spotlight-cover-fallback" aria-hidden="true">
        {/* Gradient ONLY. The card's oversized wordmark bars are banned from new art (§13.7): the
            mark is temporary, and brand reaches a cover through palette and voice instead. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 140% at 15% 15%, rgba(45,212,191,0.55) 0%, rgba(13,148,136,0.22) 40%, rgba(15,23,42,0) 72%), linear-gradient(135deg, #0f172a 0%, #134e4a 55%, #0a0f1a 100%)',
          }}
        />
      </div>
    )
  }

  return (
    <div className={box} style={aspect}>
      <iframe
        title={`${title} — cover animation`}
        srcDoc={coverHtml}
        sandbox="allow-scripts"
        scrolling="no"
        className="absolute inset-0 h-full w-full border-0"
      />
    </div>
  )
}

export default function SpotlightAnnouncement({ announcement }: SpotlightAnnouncementProps) {
  const [hidden, setHidden] = useState(false)
  const closeRef = useRef<HTMLButtonElement | null>(null)

  // ONE dismissal, fired by every exit there is — ✕, Esc, the backdrop, and both CTAs (§13.3, and
  // §4.2 for the CTAs: a click on one is "message delivered"). Fire-and-forget on purpose: the row
  // is idempotent server-side, and a failed POST must never block the navigation the operator
  // asked for or raise an error about a milestone they were already done reading. Worst case, the
  // Spotlight returns on the next page load.
  function fireDismissal() {
    try {
      void apiFetch(announcement.dismissHref, { method: 'POST' }).catch(() => {})
    } catch {
      /* never let dismissal bookkeeping reach the operator */
    }
  }

  // The census beacon (§12). Same discipline as the dismissal, for a stricter reason: this buys
  // the operator nothing at all, so it is never allowed to cost them anything either.
  function fireEvent(event: 'seen' | 'learn_more_click' | 'cta_click') {
    try {
      void apiFetch(announcement.eventsHref, { method: 'POST', body: JSON.stringify({ event }) }).catch(() => {})
    } catch {
      /* a census is never worth an operator-visible failure */
    }
  }

  // `seen` fires ONCE per announcement, not once per render: the ref makes a re-render (closing, a
  // parent update) a no-op, and the id in the dependency list re-arms it only when a genuinely
  // different announcement mounts. The server-side upsert makes a duplicate harmless anyway — this
  // keeps the request off the wire, which is the part that costs anything.
  const seenFor = useRef<number | null>(null)
  useEffect(() => {
    if (seenFor.current === announcement.id) return
    seenFor.current = announcement.id
    fireEvent('seen')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcement.id])

  function handleDismiss() {
    fireDismissal()
    setHidden(true) // closed in place — closing is an answer given here, never a navigation
  }

  // Esc is a first-class exit, not a convenience (§13.3), so it persists exactly like the ✕.
  // Registered on `window` and cleaned up on unmount: this component is mounted by the layout and
  // outlives individual pages, so a leaked listener would keep dismissing after it is gone.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      handleDismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcement.id])

  // Focus lands on the way OUT. A milestone is read, not filled in, so the one control worth
  // handing the keyboard is the one that closes it — and it makes Esc/Enter both work for someone
  // who never touches the mouse.
  useEffect(() => {
    closeRef.current?.focus()
  }, [announcement.id])

  // Only a click on the backdrop ITSELF closes (§13.3). A click that started inside the modal —
  // selecting the paragraph, poking the cursor-reactive art — bubbles up here and must not be read
  // as "close": losing a milestone by brushing the card is exactly the nagging-vs-announcing line.
  function handleBackdropClick(event: React.MouseEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) return
    handleDismiss()
  }

  // A CTA click is two independent writes: the dismissal (state — "message delivered") and the
  // census mark (observation). Both are fired alongside the navigation, neither delays it.
  function handleLearnMore() {
    fireDismissal()
    fireEvent('learn_more_click')
  }

  function handleCta() {
    fireDismissal()
    fireEvent('cta_click')
  }

  // THE PRIMARY MAY BE PURE ACKNOWLEDGEMENT (§13.5): with no `tryItHref` the button closes and
  // nothing else. A milestone often has nowhere to send anyone, and a celebration must not
  // manufacture a destination — `cta_click` is recorded either way.
  function handleAcknowledge() {
    handleCta()
    setHidden(true)
  }

  if (hidden) return null

  const items = (announcement.body.items ?? []).slice(0, 3)
  const paragraph = announcement.body.paragraph

  return (
    <div
      onClick={handleBackdropClick}
      data-testid="spotlight-backdrop"
      // FIXED and full-viewport: a milestone is centre stage over the whole app, not inside one
      // content column. z-40 — above the card (z-30), below dialogs (z-50). See the header.
      className="animate-spotlight-fade fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-6 backdrop-blur-sm"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={announcement.title}
        data-testid="spotlight-announcement"
        className="animate-spotlight-pop w-[min(880px,94vw)] overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-900/40"
      >
        <div className="relative">
          <SpotlightCover coverHtml={announcement.coverHtml} title={announcement.title} />
          {/* Sibling of the cover box, never a child: the art is free to take the cursor here
              (§13.4), and a control living inside it would be competing with an animation. */}
          <button
            ref={closeRef}
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss announcement"
            className="absolute right-3 top-3 rounded-full bg-slate-900/50 p-2 text-white/85 backdrop-blur-sm transition-colors cursor-pointer hover:bg-slate-900/75 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-8 pb-6 pt-7">
          {announcement.tagLabel && (
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {announcement.tagLabel}
            </p>
          )}
          <h2 className="mt-1.5 text-2xl font-bold leading-tight tracking-tight text-foreground">
            {announcement.title}
          </h2>

          {/* Items or a paragraph, never both — the backend validates the exclusivity, so this is
              a render fork and not an arbitration. */}
          {items.length > 0 && (
            <ul className="mt-5 space-y-4">
              {items.map((item, index) => {
                const Icon = announcementIcon(item.icon)
                return (
                  <li key={`${item.heading}-${index}`} className="flex gap-3.5">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-primary">{item.heading}</p>
                      <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          {items.length === 0 && paragraph && (
            <p className="mt-3 max-w-[60ch] text-sm leading-relaxed text-muted-foreground">{paragraph}</p>
          )}
        </div>

        {/* ALWAYS rendered, unlike the card's footer: a Spotlight always has at least the
            acknowledgement button, because a modal with no way out but the ✕ is a trap. */}
        <div className="flex items-center gap-2 border-t border-border/40 px-8 py-3.5">
          {announcement.learnMoreHref && (
            <Link
              href={announcement.learnMoreHref}
              onClick={handleLearnMore}
              className={cn(
                buttonVariants({ variant: 'ghost', size: 'sm' }),
                'rounded-lg text-muted-foreground hover:text-foreground'
              )}
            >
              Learn more
            </Link>
          )}
          {announcement.tryItHref ? (
            <Link
              href={announcement.tryItHref}
              onClick={handleCta}
              className={cn(buttonVariants({ size: 'sm' }), 'ml-auto rounded-lg')}
            >
              {announcement.ctaLabel || 'Take a look'}
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleAcknowledge}
              className={cn(buttonVariants({ size: 'sm' }), 'ml-auto rounded-lg cursor-pointer')}
            >
              {announcement.ctaLabel || 'Continue'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
